/**
 * Pagina de detalhe/edicao de cliente. Rota /dashboard/clientes/:id
 * (id = "novo" pra criacao). Substitui o Drawer anterior — mesmo form,
 * mas em pagina inteira.
 *
 * O Drawer legacy (`ClientEditDrawer`) segue exportado pela ClientsPage
 * porque a LoansPage o usa em "Info cliente" (edicao inline sem sair da
 * tela de emprestimos).
 */

import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Button, useToast,
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@payflow/ui";
import { ClientForm } from "./ClientsPage";
import {
  createClient,
  updateClient,
  getClient,
  unblockClient,
  type ClientResponse,
  type CreateClientData,
  type UpdateClientData,
} from "../../services/clients.service";
import { BlockClientDialog } from "../../components/BlockClientDialog";
import { BlockedBadge } from "../../components/BlockedBadge";

export function ClientDetailPage() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === "novo";

  const [client, setClient] = useState<ClientResponse | null>(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [blockOpen, setBlockOpen] = useState(false);
  const [unblockOpen, setUnblockOpen] = useState(false);
  const [unblocking, setUnblocking] = useState(false);

  const reload = () => {
    if (!id || isNew) return;
    getClient(id).then(setClient).catch(() => {});
  };

  const handleUnblock = async () => {
    if (!client) return;
    setUnblocking(true);
    try {
      await unblockClient(client.id);
      toast({ variant: "success", title: t("clients.unblock_success") });
      setUnblockOpen(false);
      reload();
    } catch (err) {
      toast({
        variant: "error",
        title: err instanceof Error ? err.message : t("clients.unblock_error"),
      });
    } finally {
      setUnblocking(false);
    }
  };

  useEffect(() => {
    if (isNew) return;
    let cancelled = false;
    setLoading(true);
    getClient(id!)
      .then((c) => {
        if (!cancelled) setClient(c);
      })
      .catch(() => {
        if (!cancelled) {
          toast({ variant: "error", title: t("clients.load_error") });
          navigate("/dashboard/clientes", { replace: true });
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, isNew, navigate, t, toast]);

  const handleSave = async (data: CreateClientData | UpdateClientData) => {
    setSaving(true);
    try {
      if (client) {
        await updateClient(client.id, data as UpdateClientData);
        toast({ variant: "success", title: t("clients.update_success") });
      } else {
        await createClient(data as CreateClientData);
        toast({ variant: "success", title: t("clients.create_success") });
      }
      navigate("/dashboard/clientes");
    } catch (err) {
      toast({
        variant: "error",
        title: err instanceof Error ? err.message : t("clients.save_error"),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex min-h-[calc(100vh-8rem)] flex-col gap-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/dashboard/clientes")}
            className="h-9 w-9 p-0"
            aria-label={t("common.back")}
          >
            <BackIcon />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-foreground">
                {client ? t("clients.edit_title") : t("clients.new_title")}
              </h1>
              {client?.bloqueado && <BlockedBadge />}
            </div>
            {client && (
              <p className="text-sm text-muted-foreground">{client.nome}</p>
            )}
          </div>
        </div>
        {client && !isNew && (
          <div>
            {client.bloqueado ? (
              <Button variant="outline" onClick={() => setUnblockOpen(true)}>
                {t("clients.unblock_action")}
              </Button>
            ) : (
              <Button variant="destructive" onClick={() => setBlockOpen(true)}>
                {t("clients.block_action")}
              </Button>
            )}
          </div>
        )}
      </div>

      {client?.bloqueado && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-100">
          <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
            <span className="font-medium">{t("clients.blocked_badge_title")}</span>
            {client.bloqueadoAt && (
              <span className="text-xs opacity-80">
                {t("clients.blocked_at_label")}: {new Date(client.bloqueadoAt).toLocaleString()}
              </span>
            )}
          </div>
          {client.bloqueadoMotivo && (
            <div className="mt-1 text-xs opacity-90">
              {t("clients.blocked_reason_label")}: {client.bloqueadoMotivo}
            </div>
          )}
        </div>
      )}

      <div className="flex flex-1 flex-col rounded-lg border border-border bg-card px-6 py-4">
        {loading ? (
          <div className="flex flex-1 items-center justify-center py-12">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary-200 border-t-primary-600" />
          </div>
        ) : (
          <ClientForm
            variant="page"
            client={client}
            saving={saving}
            onSave={handleSave}
          />
        )}
      </div>

      {client && (
        <>
          <BlockClientDialog
            open={blockOpen}
            onOpenChange={setBlockOpen}
            clientId={client.id}
            clientName={client.nome}
            onBlocked={reload}
          />
          <Dialog open={unblockOpen} onOpenChange={setUnblockOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{t("clients.unblock_confirm_title")}</DialogTitle>
              </DialogHeader>
              <p className="py-4 text-sm text-muted-foreground">
                {t("clients.unblock_confirm_body")}
              </p>
              <DialogFooter>
                <Button variant="outline" onClick={() => setUnblockOpen(false)}>
                  {t("common.cancel")}
                </Button>
                <Button onClick={handleUnblock} disabled={unblocking}>
                  {t("clients.unblock_confirm_button")}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </>
      )}
    </div>
  );
}

function BackIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
